/**
 * IndexedDB Cache für sehr große Datasets
 * Optional - nur nutzen wenn localStorage zu klein wird
 * 
 * Vorteile:
 * - Bis zu 50+ MB (statt 10 MB bei localStorage)
 * - Strukturierte Daten
 * - Async API
 * 
 * Nachteile:
 * - Komplexer als localStorage
 * - Async-only
 */

const DB_NAME = 'batemo-insights-cache';
const DB_VERSION = 1;
const STORE_NAME = 'api-cache';

let db = null;

/**
 * Initialize IndexedDB
 */
async function initDB() {
  if (db) return db;
  
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      db = request.result;
      resolve(db);
    };
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
}

/**
 * Store data in IndexedDB
 */
export async function setIndexedDBCache(key, data, maxAge = 8 * 60 * 60 * 1000) {
  const database = await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    const record = {
      key,
      data,
      timestamp: Date.now(),
      maxAge
    };
    
    const request = store.put(record);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get data from IndexedDB
 */
export async function getIndexedDBCache(key) {
  const database = await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    
    const request = store.get(key);
    
    request.onsuccess = () => {
      const record = request.result;
      
      if (!record) {
        resolve(null);
        return;
      }
      
      // Check if expired
      const age = Date.now() - record.timestamp;
      if (age > record.maxAge) {
        // Expired, delete it
        deleteIndexedDBCache(key);
        resolve(null);
        return;
      }
      
      resolve(record.data);
    };
    
    request.onerror = () => reject(request.error);
  });
}

/**
 * Delete data from IndexedDB
 */
export async function deleteIndexedDBCache(key) {
  const database = await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    const request = store.delete(key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Clear all IndexedDB cache
 */
export async function clearIndexedDBCache() {
  const database = await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    const request = store.clear();
    request.onsuccess = () => {
      console.log('[IndexedDB] All cache cleared');
      resolve();
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get cache statistics
 */
export async function getIndexedDBStats() {
  const database = await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    
    const request = store.getAll();
    
    request.onsuccess = () => {
      const records = request.result;
      
      const stats = {
        count: records.length,
        totalSize: 0,
        entries: []
      };
      
      records.forEach(record => {
        const size = JSON.stringify(record.data).length;
        const age = Date.now() - record.timestamp;
        
        stats.totalSize += size;
        stats.entries.push({
          key: record.key,
          size: (size / 1024).toFixed(2) + ' KB',
          age: (age / 1000 / 60).toFixed(1) + ' min',
          expired: age > record.maxAge
        });
      });
      
      stats.totalSize = (stats.totalSize / 1024 / 1024).toFixed(2) + ' MB';
      
      resolve(stats);
    };
    
    request.onerror = () => reject(request.error);
  });
}

// Global debug helper
if (typeof window !== 'undefined') {
  window.__INDEXEDDB_CACHE__ = {
    set: setIndexedDBCache,
    get: getIndexedDBCache,
    delete: deleteIndexedDBCache,
    clear: clearIndexedDBCache,
    stats: getIndexedDBStats
  };
  
  console.log('[IndexedDB] 💡 Debug tools available: window.__INDEXEDDB_CACHE__');
}
